import React, { useState, useEffect, useCallback } from 'react';
import { useVerband } from '../../context/VerbandContext';

const TABS = [
  { id: 'uebersicht',    label: 'Übersicht' },
  { id: 'verbaende',     label: 'Verbände' },
  { id: 'schulen',       label: 'Schulen' },
  { id: 'einzel',        label: 'Einzel' },
  { id: 'finanzen',      label: 'Finanzen' },
  { id: 'auswertungen',  label: 'Auswertungen' },
  { id: 'einstellungen', label: 'Einstellungen' },
];

function calcHochrechnung(v, s) {
  const gb   = parseFloat(v.abrechnung_grundbetrag) || 0;
  const rate = parseFloat(v.abrechnung_betrag) || 0;
  const ev   = parseInt(s?.turniere) || 0;
  switch (v.abrechnung_typ) {
    case 'fixbetrag':
      return { menge: 1,  rate, variabel: rate, gb, gesamt: rate + gb, mengeLabel: 'Fixbetrag' };
    case 'pro_teilnehmer':
      return { menge: s?.anmeldungen || 0, rate, variabel: (s?.anmeldungen || 0) * rate, gb, gesamt: (s?.anmeldungen || 0) * rate + gb, mengeLabel: `${s?.anmeldungen || 0} Anmeld.` };
    case 'pro_event':
      return { menge: ev, rate, variabel: ev * rate, gb, gesamt: ev * rate + gb, mengeLabel: `${ev} Events` };
    default:
      return null;
  }
}

const EMPTY_FORM = { slug:'', name:'', subdomain:'', logo_url:'', plan:'starter', is_demo: false,
  abrechnung_typ:'', abrechnung_betrag:'', abrechnung_grundbetrag:'', abrechnung_notiz:'',
  rechnung_name:'', rechnung_strasse:'', rechnung_hausnummer:'', rechnung_plz:'', rechnung_ort:'', rechnung_email:'' };

// ── Rechnungs-HTML Generator (gleicher Stil wie FinanzenDashboard) ───────────
function buildInvoiceHTML(r, verband, sender = {}) {
  const fmtD = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '–';
  const erstelltAm = fmtD(r.erstellt_am || new Date());
  const fälligAm   = fmtD(r.faellig_am);
  const betrag = parseFloat(r.betrag_gesamt || 0);
  const gb     = parseFloat(r.grundbetrag  || 0);
  const rate   = parseFloat(r.rate         || 0);
  const menge  = parseInt(r.menge          || 0);
  const typMap = { fixbetrag: 'Plattform-Nutzung (Fixbetrag)', pro_teilnehmer: 'Plattform-Nutzung (pro Teilnehmer)', pro_event: 'Plattform-Nutzung (pro Event)' };
  const typLabel = typMap[r.abrechnung_typ] || r.abrechnung_typ || 'Plattform-Nutzung';

  // Absender (aus verband-config)
  const sName    = sender.name     || 'TDA International';
  const sStrasse = sender.strasse  || '';
  const sPlz     = sender.plz      || '';
  const sOrt     = sender.ort      || '';
  const sEmail   = sender.email    || '';
  const sIban    = sender.iban     || '';
  const sBic     = sender.bic      || '';
  const sBank    = sender.bank_name|| '';
  const sAddrLine = [sStrasse, [sPlz, sOrt].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  // Turnier-Infos
  const hasTurnier = !!(r.turnier_name);

  // Rechnungsempfänger-Adresse
  const v = verband || {};
  const empfName     = v.rechnung_name       || v.name          || r.verband_name || '–';
  const empfStrasse  = v.rechnung_strasse    || '';
  const empfHausnr   = v.rechnung_hausnummer || '';
  const empfPlz      = v.rechnung_plz        || '';
  const empfOrt      = v.rechnung_ort        || '';
  const empfEmail    = v.rechnung_email      || '';

  let rows = '';
  if (gb > 0) {
    rows += `<tr><td style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee">Grundgeb&uuml;hr</td><td style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee">1</td><td style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee;text-align:right">${gb.toFixed(2)} &euro;</td><td style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee;text-align:right">${gb.toFixed(2)} &euro;</td></tr>`;
  }
  if (menge > 0 && rate > 0) {
    const turnierHint = r.turnier_name ? `<br><small style="color:#888;font-size:8pt">${r.turnier_name}${r.zeitraum_von ? ', ' + fmtD(r.zeitraum_von) : ''}</small>` : '';
    rows += `<tr><td style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee">${typLabel}${turnierHint}</td><td style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee">${menge}</td><td style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee;text-align:right">${rate.toFixed(2)} &euro;</td><td style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee;text-align:right">${(menge*rate).toFixed(2)} &euro;</td></tr>`;
  } else if (!rows) {
    const turnierHint = r.turnier_name ? ` &ndash; ${r.turnier_name}` : '';
    rows += `<tr><td colspan="3" style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee">${typLabel}${turnierHint}</td><td style="padding:2mm 3mm;font-size:9pt;border-bottom:1px solid #eee;text-align:right">${betrag.toFixed(2)} &euro;</td></tr>`;
  }

  return `<!DOCTYPE html><html lang="de"><head><meta charset="UTF-8">
  <title>Rechnung ${r.rechnungsnummer || ''}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;font-size:11pt;color:#111;padding:20mm}
    .hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8mm}
    .logo h1{font-size:18pt;font-weight:900;color:#c53030}
    .logo-addr{font-size:8pt;color:#888;margin-top:2mm;line-height:1.6}
    .meta{text-align:right}
    .meta h2{font-size:14pt;font-weight:700;letter-spacing:0.05em}
    .meta p{font-size:9pt;color:#666;margin-top:2px}
    .addr{margin-bottom:8mm;padding:4mm 5mm;border:1px solid #eee;border-radius:3px}
    .addr-label{font-size:7.5pt;color:#aaa;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:2mm}
    .addr h3{font-size:10.5pt;font-weight:700;margin-bottom:3px}
    .addr p{font-size:9pt;color:#444;line-height:1.6}
    .info{background:#f8f8f8;padding:3mm 5mm;margin-bottom:8mm;border-left:4px solid #c53030;border-radius:0 3px 3px 0}
    .info p{font-size:9pt;line-height:1.7}
    table{width:100%;border-collapse:collapse;margin-bottom:8mm}
    th{background:#c53030;color:#fff;padding:2.5mm 3mm;text-align:left;font-size:9pt}
    .tot{margin-left:auto;width:72mm;border-collapse:collapse}
    .tot td{border-bottom:none;padding:1.5mm 3mm;font-size:9pt}
    .tot .trow td{font-weight:700;font-size:11pt;border-top:2px solid #111;padding-top:2mm}
    .tot .red td{color:#c53030;font-weight:700}
    .bank{margin-top:10mm;padding:3mm 5mm;background:#f8f8f8;font-size:8pt;border-radius:3px;line-height:1.7}
    .bank strong{font-size:8.5pt}
    .foot{margin-top:12mm;font-size:8pt;color:#aaa;text-align:center;border-top:1px solid #eee;padding-top:3mm}
    @page{size:A4 portrait;margin:0}
    @media print{body{padding:15mm}}
  </style></head><body>
  <div class="hdr">
    <div class="logo">
      <h1>${sName}</h1>
      <div class="logo-addr">
        ${sAddrLine ? `<p>${sAddrLine}</p>` : ''}
        ${sEmail ? `<p>${sEmail}</p>` : ''}
      </div>
    </div>
    <div class="meta">
      <h2>RECHNUNG</h2>
      <p>Nr.&nbsp;<strong>${r.rechnungsnummer || '–'}</strong></p>
      <p>Datum:&nbsp;${erstelltAm}</p>
      ${r.faellig_am ? `<p>F&auml;llig:&nbsp;${fälligAm}</p>` : ''}
    </div>
  </div>
  <div class="addr">
    <div class="addr-label">Rechnungsempf&auml;nger</div>
    <h3>${empfName}</h3>
    ${empfStrasse ? `<p>${empfStrasse}${empfHausnr ? ' ' + empfHausnr : ''}</p>` : ''}
    ${empfPlz || empfOrt ? `<p>${empfPlz} ${empfOrt}</p>` : ''}
    ${empfEmail ? `<p>${empfEmail}</p>` : ''}
  </div>
  ${hasTurnier ? `<div class="info"><p><strong>Veranstaltung:</strong> ${r.turnier_name}</p>${r.zeitraum_von ? `<p><strong>Datum:</strong> ${fmtD(r.zeitraum_von)}${r.zeitraum_bis && r.zeitraum_bis !== r.zeitraum_von ? ' &ndash; ' + fmtD(r.zeitraum_bis) : ''}</p>` : ''}</div>` : ''}
  <table>
    <thead><tr><th>Leistung</th><th>Menge</th><th style="text-align:right">Einzelpreis</th><th style="text-align:right">Betrag</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <table class="tot">
    <tr><td>Zwischensumme:</td><td style="text-align:right">${betrag.toFixed(2)} &euro;</td></tr>
    <tr><td>MwSt. (0%):</td><td style="text-align:right">0,00 &euro;</td></tr>
    <tr class="trow"><td>Gesamtbetrag:</td><td style="text-align:right">${betrag.toFixed(2)} &euro;</td></tr>
    ${r.status === 'bezahlt' ? `<tr><td style="color:#888">Bezahlt:</td><td style="text-align:right;color:#888">- ${betrag.toFixed(2)} &euro;</td></tr><tr class="trow"><td>Offener Betrag:</td><td style="text-align:right">0,00 &euro;</td></tr>` : `<tr class="trow red"><td>Offener Betrag:</td><td style="text-align:right">${betrag.toFixed(2)} &euro;</td></tr>`}
  </table>
  ${sIban ? `<div class="bank"><strong>Bankverbindung</strong><br>${sBank ? sBank + '<br>' : ''}IBAN: ${sIban}${sBic ? ' &nbsp;|&nbsp; BIC: ' + sBic : ''}<br><strong>Verwendungszweck:</strong> ${r.rechnungsnummer || ''}</div>` : `<div class="bank"><strong>Verwendungszweck:</strong> ${r.rechnungsnummer || ''}</div>`}
  ${r.notizen ? `<div class="bank" style="margin-top:4mm"><strong>Notiz:</strong> ${r.notizen}</div>` : ''}
  <div class="foot">${sName}${sAddrLine ? ' &bull; ' + sAddrLine : ''} &bull; Vielen Dank f&uuml;r Ihre Partnerschaft!</div>
</body></html>`;
}

// ── Hauptkomponente ──────────────────────────────────────────────────────────
function VerbaendeDashboard() {
  const { setActiveVerband } = useVerband() || {};
  const [activeTab,    setActiveTab]    = useState('uebersicht');
  const [verbaende,    setVerbaende]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [stats,        setStats]        = useState({});
  const [rechnungen,   setRechnungen]   = useState({});   // { [verbandId]: [...] }
  const [verbandTurniere, setVerbandTurniere] = useState({}); // { [verbandId]: [...] }

  // Form / modal state
  const [showForm,         setShowForm]         = useState(false);
  const [modalTab,         setModalTab]         = useState('grundeinstellungen');
  const [editVerband,      setEditVerband]       = useState(null);
  const [formData,         setFormData]         = useState(EMPTY_FORM);
  const [showAdminForm,    setShowAdminForm]    = useState(null);
  const [adminForm,        setAdminForm]        = useState({ name:'', email:'', benutzername:'', passwort:'' });
  const [showRechnungForm, setShowRechnungForm] = useState(null); // verbandId
  const [rechnungForm,     setRechnungForm]     = useState({});
  const [loadingTurniere,  setLoadingTurniere]  = useState(false);
  const [previewRechnung,  setPreviewRechnung]  = useState(null); // { r, verband }

  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState('');
  const [sender, setSender] = useState({});

  // Finanzen-Filter
  const [finFilter, setFinFilter] = useState({ suche: '', verband: '', person: '', turnier: '', datumVon: '', datumBis: '', alpha: '' });
  const token = localStorage.getItem('token');

  // Absender-Daten für Rechnungen laden
  useEffect(() => {
    if (!token) return;
    fetch('/api/verband-config', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(json => { if (json.success) setSender(json.data || {}); })
      .catch(() => {});
  }, [token]);

  const loadStats = useCallback(async (id) => {
    try {
      const res  = await fetch(`/api/verbaende/${id}/stats`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setStats(prev => ({ ...prev, [id]: data.data }));
    } catch {}
  }, [token]);

  const loadRechnungen = useCallback(async (id) => {
    try {
      const res  = await fetch(`/api/verbaende/${id}/rechnungen`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setRechnungen(prev => ({ ...prev, [id]: data.data }));
    } catch {}
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch('/api/verbaende', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setVerbaende(data.data);
        data.data.forEach(v => { loadStats(v.id); loadRechnungen(v.id); });
      } else setError(data.error || 'Fehler');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token, loadStats, loadRechnungen]);

  useEffect(() => { load(); }, [load]);

  // ── Save Verband ────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const method = editVerband ? 'PUT' : 'POST';
      const url    = editVerband ? `/api/verbaende/${editVerband.id}` : '/api/verbaende';
      const body   = editVerband
        ? { name: formData.name, subdomain: formData.subdomain, logo_url: formData.logo_url,
            plan: formData.plan, aktiv: 1, is_demo: formData.is_demo ? 1 : 0,
            abrechnung_typ: formData.abrechnung_typ || null,
            abrechnung_betrag: formData.abrechnung_betrag || null,
            abrechnung_grundbetrag: formData.abrechnung_grundbetrag || null,
            abrechnung_notiz: formData.abrechnung_notiz || null,
            rechnung_name: formData.rechnung_name || null,
            rechnung_strasse: formData.rechnung_strasse || null,
            rechnung_hausnummer: formData.rechnung_hausnummer || null,
            rechnung_plz: formData.rechnung_plz || null,
            rechnung_ort: formData.rechnung_ort || null,
            rechnung_email: formData.rechnung_email || null }
        : formData;
      const res  = await fetch(url, { method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) { setMsg(editVerband ? 'Gespeichert' : 'Angelegt'); setShowForm(false); setEditVerband(null); load(); }
      else setMsg(data.error || 'Fehler');
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  // ── Save Admin ──────────────────────────────────────────────────
  const handleAdminSave = async () => {
    setSaving(true); setMsg('');
    try {
      const res  = await fetch(`/api/verbaende/${showAdminForm}/admin`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(adminForm) });
      const data = await res.json();
      if (data.success) { setMsg('Admin angelegt'); setShowAdminForm(null); setAdminForm({ name:'', email:'', benutzername:'', passwort:'' }); }
      else setMsg(data.error || 'Fehler');
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  // ── Rechnung erstellen ──────────────────────────────────────────
  const openRechnungForm = async (v) => {
    const s  = stats[v.id] || {};
    const hr = calcHochrechnung(v, s);
    const today = new Date().toISOString().slice(0, 10);
    // 14 Tage Zahlungsziel
    const dueDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

    // Turniere laden (gecacht)
    let turniere = verbandTurniere[v.id];
    if (!turniere) {
      setLoadingTurniere(true);
      try {
        const res  = await fetch(`/api/verbaende/${v.id}/turniere`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) {
          turniere = data.data;
          setVerbandTurniere(prev => ({ ...prev, [v.id]: turniere }));
        }
      } catch {}
      setLoadingTurniere(false);
    }

    // Erstes Turnier auto-vorauswählen
    const firstT = turniere?.[0];
    const initMenge = v.abrechnung_typ === 'pro_teilnehmer'
      ? (firstT?.anmeldungen_count || s?.anmeldungen || 0)
      : hr?.menge || 0;
    const initGesamt = ((parseFloat(v.abrechnung_grundbetrag) || 0) + (parseFloat(v.abrechnung_betrag) || 0) * initMenge).toFixed(2);

    setRechnungForm({
      verband_id:    v.id,
      verband_name:  v.name,
      turnier_id:    firstT?.turnier_id || '',
      turnier_name:  firstT?.name || '',
      abrechnung_typ: v.abrechnung_typ || '',
      grundbetrag:   v.abrechnung_grundbetrag || 0,
      rate:          v.abrechnung_betrag || 0,
      menge:         initMenge,
      betrag_gesamt: initGesamt,
      notizen:       '',
      faellig_am:    dueDate,
    });
    setShowRechnungForm(v.id);
  };

  const handleRechnungSave = async () => {
    setSaving(true); setMsg('');
    try {
      const res  = await fetch(`/api/verbaende/${rechnungForm.verband_id}/rechnungen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(rechnungForm),
      });
      const data = await res.json();
      if (data.success) {
        setMsg(`Rechnung ${data.data.rechnungsnummer} erstellt`);
        setShowRechnungForm(null);
        loadRechnungen(rechnungForm.verband_id);
        // Kurz nach dem Laden Vorschau anzeigen
        const verband = verbaende.find(v => v.id === rechnungForm.verband_id);
        setTimeout(() => setPreviewRechnung({ r: data.data, verband }), 300);
      } else setMsg(data.error || 'Fehler');
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (rid, status, verbandId) => {
    try {
      await fetch(`/api/verbaende/rechnungen/${rid}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      loadRechnungen(verbandId);
    } catch {}
  };

  const handlePrint = (r, verband) => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(buildInvoiceHTML(r, verband, sender));
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  // ── Open edit ───────────────────────────────────────────────────
  const openEdit = (v) => {
    setEditVerband(v);
    setFormData({
      slug: v.slug, name: v.name, subdomain: v.subdomain, logo_url: v.logo_url || '',
      plan: v.plan, is_demo: Number(v.is_demo) === 1,
      abrechnung_typ: v.abrechnung_typ || '',
      abrechnung_betrag: v.abrechnung_betrag || '',
      abrechnung_grundbetrag: v.abrechnung_grundbetrag || '',
      abrechnung_notiz: v.abrechnung_notiz || '',
      rechnung_name: v.rechnung_name || '',
      rechnung_strasse: v.rechnung_strasse || '',
      rechnung_hausnummer: v.rechnung_hausnummer || '',
      rechnung_plz: v.rechnung_plz || '',
      rechnung_ort: v.rechnung_ort || '',
      rechnung_email: v.rechnung_email || '',
    });
    setModalTab('grundeinstellungen');
    setShowForm(true);
  };

  // ── Aggregate stats — Demo-Verbände ausschließen ─────────────────
  const echteVerbaende = verbaende.filter(v => Number(v.is_demo) !== 1);
  const totalStats = Object.entries(stats)
    .filter(([id]) => echteVerbaende.some(v => String(v.id) === String(id)))
    .reduce((acc, [, s]) => ({
      turniere:     (acc.turniere     || 0) + (Number(s.turniere)     || 0),
      vereine:      (acc.vereine      || 0) + (Number(s.vereine)      || 0),
      wettkaempfer: (acc.wettkaempfer || 0) + (Number(s.wettkaempfer) || 0),
      anmeldungen:  (acc.anmeldungen  || 0) + (Number(s.anmeldungen)  || 0),
    }), {});

  // ── Auswertungen — berechnete Werte ────────────────────────────
  const allRechnungen  = Object.values(rechnungen).flat();
  const paidR          = allRechnungen.filter(r => r.status === 'bezahlt');
  const openR          = allRechnungen.filter(r => ['entwurf','gesendet','offen'].includes(r.status));
  const totalUmsatz    = paidR.reduce((s, r) => s + parseFloat(r.betrag_gesamt || 0), 0);
  const totalAusstehend= openR.reduce((s, r) => s + parseFloat(r.betrag_gesamt || 0), 0);

  const verbandFinanz = verbaende.map(v => {
    const vr = rechnungen[v.id] || [];
    return {
      ...v,
      umsatz: vr.filter(r => r.status === 'bezahlt').reduce((s, r) => s + parseFloat(r.betrag_gesamt || 0), 0),
      offen:  vr.filter(r => ['entwurf','gesendet','offen'].includes(r.status)).reduce((s, r) => s + parseFloat(r.betrag_gesamt || 0), 0),
      cnt: vr.length,
    };
  }).sort((a, b) => b.umsatz - a.umsatz);
  const maxUmsatz = Math.max(...verbandFinanz.map(v => v.umsatz), 1);

  const verbandNutzung = echteVerbaende.map(v => ({ ...v, s: stats[v.id] || {} }))
    .sort((a, b) => (Number(b.s.anmeldungen) || 0) - (Number(a.s.anmeldungen) || 0));
  const maxAnmeld = Math.max(...verbandNutzung.map(v => Number(v.s.anmeldungen) || 0), 1);

  const planCount = verbaende.filter(v => Number(v.is_demo) !== 1)
    .reduce((acc, v) => { acc[v.plan] = (acc[v.plan] || 0) + 1; return acc; }, {});

  const abrCount = verbaende.filter(v => v.abrechnung_typ)
    .reduce((acc, v) => { acc[v.abrechnung_typ] = (acc[v.abrechnung_typ] || 0) + 1; return acc; }, {});

  const monthlyData = {};
  paidR.forEach(r => {
    const d   = new Date(r.bezahlt_am || r.erstellt_am);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthlyData[key] = (monthlyData[key] || 0) + parseFloat(r.betrag_gesamt || 0);
  });
  const months6     = Object.keys(monthlyData).sort().slice(-6);
  const maxMonthly  = Math.max(...months6.map(m => monthlyData[m]), 1);

  // ── Status badge ────────────────────────────────────────────────
  const statusColor = { entwurf:'#6b7280', gesendet:'#3b82f6', bezahlt:'#10b981', storniert:'#ef4444' };

  const fmt     = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '—';
  const fmtEuro = (n) => Number(n || 0).toLocaleString('de-DE', { style:'currency', currency:'EUR' });

  // Turnier-Dropdown: wenn Verband gewählt ist
  const currentTurniere = verbandTurniere[rechnungForm.verband_id] || [];

  // ── Finanzen-Filter-Logik ────────────────────────────────────────
  const finAlphaLetters = [...new Set(
    verbaende.map(v => (v.name || '').charAt(0).toUpperCase()).filter(Boolean)
  )].sort();

  const filteredFinVerbaende = verbaende.filter(v => {
    const { suche, verband, person, turnier, datumVon, datumBis, alpha } = finFilter;
    if (alpha && (v.name || '').charAt(0).toUpperCase() !== alpha) return false;
    if (verband && !(v.name || '').toLowerCase().includes(verband.toLowerCase())) return false;
    if (person) {
      const p = (v.rechnung_name || v.name || '').toLowerCase();
      if (!p.includes(person.toLowerCase())) return false;
    }
    const vRech = rechnungen[v.id] || [];
    if (turnier) {
      const match = vRech.some(r => (r.turnier_name || '').toLowerCase().includes(turnier.toLowerCase()));
      if (!match) return false;
    }
    if (datumVon || datumBis) {
      const match = vRech.some(r => {
        const d = r.faellig_am ? new Date(r.faellig_am) : null;
        if (!d) return false;
        if (datumVon && d < new Date(datumVon)) return false;
        if (datumBis && d > new Date(datumBis + 'T23:59:59')) return false;
        return true;
      });
      if (!match) return false;
    }
    if (suche) {
      const q = suche.toLowerCase();
      const inVerband = (v.name || '').toLowerCase().includes(q);
      const inPerson  = (v.rechnung_name || '').toLowerCase().includes(q);
      const inRech    = vRech.some(r =>
        (r.rechnungsnummer || '').toLowerCase().includes(q) ||
        (r.turnier_name    || '').toLowerCase().includes(q) ||
        (r.notizen         || '').toLowerCase().includes(q)
      );
      if (!inVerband && !inPerson && !inRech) return false;
    }
    return true;
  });

  const setFF = (key, val) => setFinFilter(prev => ({ ...prev, [key]: val }));

  return (
    <div className="vd-wrap">
      {/* Tab-Bar */}
      <div className="vd-tabs">
        {TABS.map(t => (
          <button key={t.id} className={`vd-tab${activeTab === t.id ? ' active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
        {activeTab === 'verbaende' && (
          <button className="vd-tab-action" onClick={() => { setEditVerband(null); setFormData(EMPTY_FORM); setModalTab('grundeinstellungen'); setShowForm(true); }}>
            + Neuer Verband
          </button>
        )}
      </div>

      {msg && <div className="vd-msg">{msg}</div>}

      {/* ── Übersicht ── */}
      {activeTab === 'uebersicht' && (
        <div className="vd-content">
          <div className="vd-stat-row">
            <div className="vd-stat"><span className="vd-stat-val">{echteVerbaende.length}</span><span className="vd-stat-lbl">Verbände</span></div>
            <div className="vd-stat"><span className="vd-stat-val">{totalStats.turniere ?? 0}</span><span className="vd-stat-lbl">Turniere</span></div>
            <div className="vd-stat"><span className="vd-stat-val">{totalStats.vereine ?? 0}</span><span className="vd-stat-lbl">Schulen</span></div>
            <div className="vd-stat"><span className="vd-stat-val">{totalStats.wettkaempfer ?? 0}</span><span className="vd-stat-lbl">Kämpfer</span></div>
            <div className="vd-stat"><span className="vd-stat-val">{totalStats.anmeldungen ?? 0}</span><span className="vd-stat-lbl">Anmeldungen</span></div>
          </div>
          <div className="vd-overview-list">
            {echteVerbaende.map(v => {
              const s  = stats[v.id] || {};
              const hr = calcHochrechnung(v, s);
              return (
                <div key={v.id} className="vd-overview-row">
                  <span className="vd-ov-name">{v.name}</span>
                  <span className="vd-ov-sub">{v.subdomain}</span>
                  <span className={`vd-plan plan-${v.plan}`}>{v.plan}</span>
                  <span className="vd-ov-num">{s.turniere ?? '…'} Turniere</span>
                  <span className="vd-ov-num">{s.vereine ?? '…'} Schulen</span>
                  <span className="vd-ov-num">{s.wettkaempfer ?? '…'} Kämpfer</span>
                  {hr && <span className="vd-ov-bill">💶 {fmtEuro(hr.gesamt)}/Period.</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Verbände ── */}
      {activeTab === 'verbaende' && (
        <div className="vd-content">
          {loading ? <div className="vd-loading">Laden…</div> : error ? <div className="vd-error">{error}</div> : (
            <div className="vd-grid">
              {verbaende.map(v => {
                const s = stats[v.id] || {};
                return (
                  <div key={v.id} className={`vd-card${!v.aktiv ? ' inactive' : ''}`}>
                    <div className="vd-card-top">
                      {v.logo_url && <img src={v.logo_url} alt={v.name} className="vd-logo" />}
                      <div className="vd-card-info">
                        <h3>{v.name}</h3>
                        <div className="vd-card-meta">
                          <span className="vd-slug">{v.slug}</span>
                          <span className={`vd-plan plan-${v.plan}`}>{v.plan}</span>
                          {!v.aktiv && <span className="vd-inactive">inaktiv</span>}
                          {Number(v.is_demo) === 1 && <span className="vd-demo">DEMO</span>}
                        </div>
                      </div>
                    </div>
                    <div className="vd-subdomain">{v.subdomain}</div>
                    <div className="vd-stats">
                      <div className="vd-s"><b>{s.turniere ?? '…'}</b><span>Turniere</span></div>
                      <div className="vd-s"><b>{s.vereine ?? '…'}</b><span>Schulen</span></div>
                      <div className="vd-s"><b>{s.wettkaempfer ?? '…'}</b><span>Kämpfer</span></div>
                      <div className="vd-s"><b>{s.anmeldungen ?? '…'}</b><span>Anmeld.</span></div>
                    </div>
                    <div className="vd-actions">
                      <button className="vd-btn switch" onClick={() => { if (setActiveVerband) setActiveVerband(v); localStorage.setItem('activeVerbandId', v.id); setMsg(`Aktiv: ${v.name}`); }}>⇄ Switch</button>
                      <button className="vd-btn edit"   onClick={() => openEdit(v)}>✏️ Bearbeiten</button>
                      <button className="vd-btn admin"  onClick={() => setShowAdminForm(v.id)}>👤 Admin</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Placeholder Tabs ── */}
      {['schulen','einzel'].includes(activeTab) && (
        <div className="vd-content vd-placeholder">
          <div className="vd-ph-icon">{activeTab === 'schulen' ? '🏫' : '🥋'}</div>
          <h3>{TABS.find(t => t.id === activeTab)?.label}</h3>
          <p>Wird in Kürze implementiert.</p>
        </div>
      )}

      {/* ── Auswertungen ── */}
      {activeTab === 'auswertungen' && (
        <div className="vd-content">

          {/* KPI-Karten */}
          <div className="vd-aw-kpi-row">
            <div className="vd-aw-kpi green">
              <span className="vd-aw-kpi-val">{fmtEuro(totalUmsatz)}</span>
              <span className="vd-aw-kpi-lbl">Gesamtumsatz (bezahlt)</span>
            </div>
            <div className="vd-aw-kpi yellow">
              <span className="vd-aw-kpi-val">{fmtEuro(totalAusstehend)}</span>
              <span className="vd-aw-kpi-lbl">Ausstehend / Offen</span>
            </div>
            <div className="vd-aw-kpi blue">
              <span className="vd-aw-kpi-val">{echteVerbaende.length}</span>
              <span className="vd-aw-kpi-lbl">Verbände (aktiv)</span>
            </div>
            <div className="vd-aw-kpi purple">
              <span className="vd-aw-kpi-val">{allRechnungen.length}</span>
              <span className="vd-aw-kpi-lbl">Rechnungen gesamt</span>
            </div>
          </div>

          {/* Umsatz + Nutzungsranking */}
          <div className="vd-aw-grid">
            <div className="vd-aw-card">
              <h4 className="vd-aw-title">Umsatz pro Verband</h4>
              {verbandFinanz.length === 0
                ? <p className="vd-aw-empty">Noch keine Rechnungsdaten</p>
                : verbandFinanz.map(v => (
                  <div key={v.id} className="vd-aw-bar-row">
                    <div className="vd-aw-bar-label">
                      {v.name}
                      {Number(v.is_demo) === 1 && <span className="vd-demo" style={{fontSize:'0.55rem',marginLeft:'0.25rem'}}>DEMO</span>}
                    </div>
                    <div className="vd-aw-bar-track">
                      <div className="vd-aw-bar-fill green" style={{width:(v.umsatz/maxUmsatz*100)+'%'}} />
                      {v.offen > 0 && <div className="vd-aw-bar-fill yellow" style={{width:(v.offen/maxUmsatz*100)+'%',marginLeft:'2px'}} />}
                    </div>
                    <span className="vd-aw-bar-val">{fmtEuro(v.umsatz)}</span>
                  </div>
                ))
              }
              {verbandFinanz.some(v => v.offen > 0) && (
                <div className="vd-aw-legend">
                  <span className="vd-aw-leg-dot green" />bezahlt
                  <span className="vd-aw-leg-dot yellow" style={{marginLeft:'0.75rem'}} />ausstehend
                </div>
              )}
            </div>

            <div className="vd-aw-card">
              <h4 className="vd-aw-title">Nutzungsranking — Anmeldungen</h4>
              {verbandNutzung.length === 0
                ? <p className="vd-aw-empty">Keine Daten</p>
                : verbandNutzung.map((v, i) => {
                    const anm = Number(v.s.anmeldungen) || 0;
                    const turn = Number(v.s.turniere) || 0;
                    return (
                      <div key={v.id} className="vd-aw-bar-row">
                        <div className="vd-aw-bar-label">
                          <span className="vd-aw-rank">#{i+1}</span>
                          <span>{v.name}</span>
                        </div>
                        <div className="vd-aw-bar-track">
                          <div className="vd-aw-bar-fill blue" style={{width:(anm/maxAnmeld*100)+'%'}} />
                        </div>
                        <span className="vd-aw-bar-val">{anm} <span style={{color:'#555',fontSize:'0.72rem'}}>/ {turn} T</span></span>
                      </div>
                    );
                  })
              }
            </div>
          </div>

          {/* Monatlicher Umsatz */}
          {months6.length > 0 && (
            <div className="vd-aw-card" style={{marginTop:'1rem'}}>
              <h4 className="vd-aw-title">Monatlicher Umsatz</h4>
              <div className="vd-aw-months">
                {months6.map(m => {
                  const [y, mo] = m.split('-');
                  const label = new Date(parseInt(y), parseInt(mo)-1).toLocaleDateString('de-DE', {month:'short', year:'2-digit'});
                  const pct   = monthlyData[m] / maxMonthly * 100;
                  return (
                    <div key={m} className="vd-aw-month-col">
                      <div className="vd-aw-month-bar-wrap">
                        <div className="vd-aw-month-bar" style={{height: Math.max(pct, 3)+'%'}} title={fmtEuro(monthlyData[m])} />
                      </div>
                      <span className="vd-aw-month-val">{fmtEuro(monthlyData[m]).replace(/\s*€/, '')}</span>
                      <span className="vd-aw-month-lbl">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {months6.length === 0 && (
            <div className="vd-aw-card" style={{marginTop:'1rem'}}>
              <h4 className="vd-aw-title">Monatlicher Umsatz</h4>
              <p className="vd-aw-empty">Noch keine bezahlten Rechnungen vorhanden.</p>
            </div>
          )}

          {/* Plan + Abrechnungsarten */}
          <div className="vd-aw-grid" style={{marginTop:'1rem'}}>
            <div className="vd-aw-card">
              <h4 className="vd-aw-title">Plan-Verteilung</h4>
              <div className="vd-aw-pills">
                {['enterprise','pro','starter','free'].map(p => planCount[p] ? (
                  <div key={p} className="vd-aw-pill">
                    <span className={`vd-plan plan-${p}`}>{p}</span>
                    <span className="vd-aw-pill-cnt">{planCount[p]} Verbänd{planCount[p] === 1 ? 'e' : 'e'}</span>
                  </div>
                ) : null)}
                {Object.keys(planCount).length === 0 && <p className="vd-aw-empty">Keine Daten</p>}
              </div>
            </div>

            <div className="vd-aw-card">
              <h4 className="vd-aw-title">Abrechnungsarten</h4>
              <div className="vd-aw-pills">
                {Object.entries(abrCount).map(([typ, cnt]) => (
                  <div key={typ} className="vd-aw-pill">
                    <span style={{fontSize:'0.82rem',fontWeight:600}}>
                      {typ === 'fixbetrag' ? 'Fixbetrag' : typ === 'pro_teilnehmer' ? 'Pro Teilnehmer' : 'Pro Event'}
                    </span>
                    <span className="vd-aw-pill-cnt">{cnt}×</span>
                  </div>
                ))}
                {Object.keys(abrCount).length === 0 && <p className="vd-aw-empty">Keine Abrechnungsart konfiguriert</p>}
              </div>
            </div>
          </div>

          {/* Detailtabelle */}
          <div className="vd-aw-card" style={{marginTop:'1rem'}}>
            <h4 className="vd-aw-title">Vollständige Übersicht</h4>
            <div style={{overflowX:'auto'}}>
              <table className="vd-table">
                <thead>
                  <tr>
                    <th>Verband</th>
                    <th>Plan</th>
                    <th style={{textAlign:'right'}}>Turniere</th>
                    <th style={{textAlign:'right'}}>Schulen</th>
                    <th style={{textAlign:'right'}}>Kämpfer</th>
                    <th style={{textAlign:'right'}}>Anmeld.</th>
                    <th style={{textAlign:'right'}}>Rechnungen</th>
                    <th style={{textAlign:'right'}}>Umsatz</th>
                    <th style={{textAlign:'right'}}>Offen</th>
                  </tr>
                </thead>
                <tbody>
                  {verbaende.map(v => {
                    const s   = stats[v.id] || {};
                    const vr  = rechnungen[v.id] || [];
                    const ums = vr.filter(r => r.status === 'bezahlt').reduce((a, r) => a + parseFloat(r.betrag_gesamt || 0), 0);
                    const off = vr.filter(r => ['entwurf','gesendet','offen'].includes(r.status)).reduce((a, r) => a + parseFloat(r.betrag_gesamt || 0), 0);
                    return (
                      <tr key={v.id} style={Number(v.is_demo) === 1 ? {opacity:0.55} : {}}>
                        <td>
                          <span style={{fontWeight:600}}>{v.name}</span>
                          {Number(v.is_demo) === 1 && <span className="vd-demo" style={{marginLeft:'0.4rem',fontSize:'0.55rem'}}>DEMO</span>}
                        </td>
                        <td><span className={`vd-plan plan-${v.plan}`}>{v.plan}</span></td>
                        <td style={{textAlign:'right'}}>{Number(s.turniere) || 0}</td>
                        <td style={{textAlign:'right'}}>{Number(s.vereine) || 0}</td>
                        <td style={{textAlign:'right'}}>{Number(s.wettkaempfer) || 0}</td>
                        <td style={{textAlign:'right'}}>{Number(s.anmeldungen) || 0}</td>
                        <td style={{textAlign:'right',color:'var(--text-muted,#888)'}}>{vr.length}</td>
                        <td style={{textAlign:'right',color:'#10b981',fontWeight:600}}>{ums > 0 ? fmtEuro(ums) : <span style={{color:'#444'}}>—</span>}</td>
                        <td style={{textAlign:'right',color: off > 0 ? '#f87171' : '#444',fontWeight: off > 0 ? 600 : 400}}>{off > 0 ? fmtEuro(off) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{borderTop:'2px solid var(--border,#2a2a4a)'}}>
                    <td colSpan={7} style={{fontWeight:700,paddingTop:'0.75rem'}}>Gesamt (ohne Demo)</td>
                    <td style={{textAlign:'right',fontWeight:700,color:'#10b981',paddingTop:'0.75rem'}}>{fmtEuro(totalUmsatz)}</td>
                    <td style={{textAlign:'right',fontWeight:700,color: totalAusstehend > 0 ? '#f87171' : '#444',paddingTop:'0.75rem'}}>{totalAusstehend > 0 ? fmtEuro(totalAusstehend) : '—'}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* ── Finanzen ── */}
      {activeTab === 'finanzen' && (
        <div className="vd-content">

          {/* Filter-Bar */}
          <div className="vd-fin-filter-bar">
            <div className="vd-fin-filter-row">
              <input className="vd-fin-input" placeholder="🔍 Suche …" value={finFilter.suche}
                onChange={e => setFF('suche', e.target.value)} />
              <input className="vd-fin-input" placeholder="Verband" value={finFilter.verband}
                onChange={e => setFF('verband', e.target.value)} />
              <input className="vd-fin-input" placeholder="Person" value={finFilter.person}
                onChange={e => setFF('person', e.target.value)} />
              <input className="vd-fin-input" placeholder="Turnier" value={finFilter.turnier}
                onChange={e => setFF('turnier', e.target.value)} />
              <input className="vd-fin-input" type="date" title="Datum von" value={finFilter.datumVon}
                onChange={e => setFF('datumVon', e.target.value)} />
              <input className="vd-fin-input" type="date" title="Datum bis" value={finFilter.datumBis}
                onChange={e => setFF('datumBis', e.target.value)} />
              {(finFilter.suche || finFilter.verband || finFilter.person || finFilter.turnier || finFilter.datumVon || finFilter.datumBis || finFilter.alpha) && (
                <button className="vd-fin-clear" onClick={() => setFinFilter({ suche:'', verband:'', person:'', turnier:'', datumVon:'', datumBis:'', alpha:'' })}>✕ Filter</button>
              )}
            </div>
            {/* ABC-Filter — nur Buchstaben mit vorhandenen Einträgen */}
            <div className="vd-fin-alpha-bar">
              {finAlphaLetters.map(l => (
                <button key={l}
                  className={`vd-fin-alpha-btn${finFilter.alpha === l ? ' active' : ''}`}
                  onClick={() => setFF('alpha', finFilter.alpha === l ? '' : l)}>
                  {l}
                </button>
              ))}
            </div>
            {filteredFinVerbaende.length !== verbaende.length && (
              <div className="vd-fin-filter-hint">{filteredFinVerbaende.length} von {verbaende.length} Verbänden</div>
            )}
          </div>

          {filteredFinVerbaende.map(v => {
            const s     = stats[v.id] || {};
            const hr    = calcHochrechnung(v, s);
            const vRech = (rechnungen[v.id] || []).filter(r => {
              const { turnier, datumVon, datumBis } = finFilter;
              if (turnier && !(r.turnier_name || '').toLowerCase().includes(turnier.toLowerCase())) return false;
              if (datumVon || datumBis) {
                const d = r.faellig_am ? new Date(r.faellig_am) : null;
                if (!d) return false;
                if (datumVon && d < new Date(datumVon)) return false;
                if (datumBis && d > new Date(datumBis + 'T23:59:59')) return false;
              }
              return true;
            });
            const offen = vRech.filter(r => r.status === 'offen' || r.status === 'gesendet' || r.status === 'entwurf')
                            .reduce((a, r) => a + parseFloat(r.betrag_gesamt || 0), 0);
            return (
              <div key={v.id} className="vd-fin-block">
                <div className="vd-fin-header">
                  <div>
                    <span className="vd-fin-name">{v.name}</span>
                    {Number(v.is_demo) === 1 && <span className="vd-demo" style={{marginLeft:'0.5rem'}}>DEMO</span>}
                    {v.abrechnung_typ && (
                      <span className="vd-fin-typ" style={{marginLeft:'0.5rem'}}>
                        {v.abrechnung_typ === 'fixbetrag' ? 'Fixbetrag' : v.abrechnung_typ === 'pro_teilnehmer' ? 'Pro Teilnehmer' : 'Pro Event'}
                      </span>
                    )}
                  </div>
                  <button className="vd-btn-rechnung" onClick={() => openRechnungForm(v)}>
                    {loadingTurniere ? '…' : '+ Rechnung erstellen'}
                  </button>
                </div>

                {/* Hochrechnung */}
                {hr ? (
                  <div className="vd-hochrechnung">
                    <div className="vd-hr-row">
                      <span className="vd-hr-lbl">Variabel</span>
                      <span className="vd-hr-val">{hr.mengeLabel} × {fmtEuro(hr.rate)} = <strong>{fmtEuro(hr.variabel)}</strong></span>
                    </div>
                    {hr.gb > 0 && (
                      <div className="vd-hr-row">
                        <span className="vd-hr-lbl">Grundbetrag</span>
                        <span className="vd-hr-val"><strong>{fmtEuro(hr.gb)}</strong></span>
                      </div>
                    )}
                    <div className="vd-hr-row total">
                      <span className="vd-hr-lbl">Hochrechnung gesamt</span>
                      <span className="vd-hr-val"><strong>{fmtEuro(hr.gesamt)}</strong></span>
                    </div>
                    {offen > 0 && (
                      <div className="vd-hr-row offen">
                        <span className="vd-hr-lbl">Offen (Rechnungen)</span>
                        <span className="vd-hr-val" style={{color:'#f87171'}}><strong>{fmtEuro(offen)}</strong></span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="vd-no-abrechnung">Keine Abrechnungsart hinterlegt — <button className="vd-link" onClick={() => openEdit(v)}>Jetzt konfigurieren</button></div>
                )}

                {/* Rechnungsliste */}
                {vRech.length > 0 && (
                  <table className="vd-table vd-fin-table">
                    <thead><tr><th>Nummer</th><th>Turnier / Leistung</th><th>Betrag</th><th>Fällig</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                      {vRech.map(r => (
                        <tr key={r.rechnung_id}>
                          <td><code>{r.rechnungsnummer}</code></td>
                          <td style={{fontSize:'0.8rem'}}>
                            {r.turnier_name
                              ? <span>{r.turnier_name}{r.zeitraum_von ? <span style={{color:'#888'}}> · {fmt(r.zeitraum_von)}</span> : ''}</span>
                              : <span style={{color:'#888'}}>{r.zeitraum_von ? `${fmt(r.zeitraum_von)} – ${fmt(r.zeitraum_bis)}` : '—'}</span>
                            }
                          </td>
                          <td><strong>{fmtEuro(r.betrag_gesamt)}</strong></td>
                          <td style={{fontSize:'0.8rem'}}>{fmt(r.faellig_am)}</td>
                          <td>
                            <span className="vd-status-badge" style={{background: statusColor[r.status] + '33', color: statusColor[r.status]}}>
                              {r.status}
                            </span>
                          </td>
                          <td className="vd-row-actions">
                            <button className="vd-link" onClick={() => setPreviewRechnung({ r, verband: v })}>👁 Vorschau</button>
                            <button className="vd-link" onClick={() => handlePrint(r, v)}>🖨</button>
                            {r.status !== 'bezahlt' && r.status !== 'storniert' && (
                              <button className="vd-link vd-link-green" onClick={() => handleStatusChange(r.rechnung_id, 'bezahlt', v.id)}>✓ Bezahlt</button>
                            )}
                            {r.status !== 'storniert' && (
                              <button className="vd-link vd-link-red" onClick={() => { if (window.confirm(`Rechnung ${r.rechnungsnummer} wirklich stornieren?`)) handleStatusChange(r.rechnung_id, 'storniert', v.id); }}>✕ Stornieren</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Einstellungen ── */}
      {activeTab === 'einstellungen' && (
        <div className="vd-content vd-placeholder">
          <div className="vd-ph-icon">⚙️</div>
          <h3>Einstellungen</h3>
          <p>Globale Plattform-Einstellungen folgen.</p>
        </div>
      )}

      {/* ── Modal: Verband bearbeiten / anlegen ── */}
      {showForm && (
        <div className="vd-overlay" onClick={() => { setShowForm(false); setEditVerband(null); }}>
          <div className="vd-modal" onClick={e => e.stopPropagation()}>
            <h3>{editVerband ? 'Verband bearbeiten' : 'Neuer Verband'}</h3>

            <div className="vd-modal-tabs">
              <button className={`vd-modal-tab${modalTab === 'grundeinstellungen' ? ' active' : ''}`} onClick={() => setModalTab('grundeinstellungen')}>Grundeinstellungen</button>
              <button className={`vd-modal-tab${modalTab === 'adresse' ? ' active' : ''}`} onClick={() => setModalTab('adresse')}>Rechnungsadresse</button>
              <button className={`vd-modal-tab${modalTab === 'finanzen' ? ' active' : ''}`} onClick={() => setModalTab('finanzen')}>Finanzen</button>
            </div>

            {modalTab === 'grundeinstellungen' && (
              <div className="vd-form">
                {!editVerband && <label>Slug<input value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})} placeholder="wkc" /></label>}
                <label>Name<input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="WKC International" /></label>
                <label>Subdomain<input value={formData.subdomain} onChange={e => setFormData({...formData, subdomain: e.target.value})} placeholder="wkc.events.tda-intl.org" /></label>
                <label>Logo-URL<input value={formData.logo_url} onChange={e => setFormData({...formData, logo_url: e.target.value})} placeholder="https://…" /></label>
                <label>Plan
                  <select value={formData.plan} onChange={e => setFormData({...formData, plan: e.target.value})}>
                    <option value="free">Free</option>
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </label>
                <label className="vd-checkbox-label">
                  <input type="checkbox" checked={!!formData.is_demo} onChange={e => setFormData({...formData, is_demo: e.target.checked})} />
                  Demo-Verband (erscheint nicht in der Statistik)
                </label>
              </div>
            )}

            {modalTab === 'adresse' && (
              <div className="vd-form">
                <p style={{fontSize:'0.8rem',color:'var(--text-muted,#888)',marginBottom:'0.25rem'}}>Wird auf Rechnungen als Empfänger-Adresse gedruckt.</p>
                <label>Rechnungsname (falls abweichend vom Verbandsnamen)<input value={formData.rechnung_name} onChange={e => setFormData({...formData, rechnung_name: e.target.value})} placeholder={formData.name || 'z.B. WKC International e.V.'} /></label>
                <div className="vd-form-row">
                  <label style={{flex:3}}>Straße<input value={formData.rechnung_strasse} onChange={e => setFormData({...formData, rechnung_strasse: e.target.value})} placeholder="Musterstraße" /></label>
                  <label style={{flex:1}}>Nr.<input value={formData.rechnung_hausnummer} onChange={e => setFormData({...formData, rechnung_hausnummer: e.target.value})} placeholder="12" /></label>
                </div>
                <div className="vd-form-row">
                  <label style={{flex:1}}>PLZ<input value={formData.rechnung_plz} onChange={e => setFormData({...formData, rechnung_plz: e.target.value})} placeholder="12345" /></label>
                  <label style={{flex:3}}>Ort<input value={formData.rechnung_ort} onChange={e => setFormData({...formData, rechnung_ort: e.target.value})} placeholder="Musterstadt" /></label>
                </div>
                <label>E-Mail (Rechnungsversand)<input type="email" value={formData.rechnung_email} onChange={e => setFormData({...formData, rechnung_email: e.target.value})} placeholder="finanzen@verband.org" /></label>
              </div>
            )}

            {modalTab === 'finanzen' && (
              <div className="vd-form">
                <label>Abrechnungsart
                  <select value={formData.abrechnung_typ} onChange={e => setFormData({...formData, abrechnung_typ: e.target.value})}>
                    <option value="">— keine —</option>
                    <option value="fixbetrag">Fixbetrag (€/Monat)</option>
                    <option value="pro_teilnehmer">Pro Teilnehmer</option>
                    <option value="pro_event">Pro Event</option>
                  </select>
                </label>
                {formData.abrechnung_typ && (
                  <label>
                    {formData.abrechnung_typ === 'fixbetrag' ? 'Betrag (€/Monat)' : formData.abrechnung_typ === 'pro_teilnehmer' ? 'Betrag pro Teilnehmer (€)' : 'Betrag pro Event (€)'}
                    <input type="number" step="0.01" min="0" value={formData.abrechnung_betrag} onChange={e => setFormData({...formData, abrechnung_betrag: e.target.value})} placeholder="0.00" />
                  </label>
                )}
                <label>
                  Grundbetrag (€/Monat, zusätzlich)
                  <input type="number" step="0.01" min="0" value={formData.abrechnung_grundbetrag} onChange={e => setFormData({...formData, abrechnung_grundbetrag: e.target.value})} placeholder="0.00 (optional)" />
                </label>
                <label>Interne Notiz<input value={formData.abrechnung_notiz} onChange={e => setFormData({...formData, abrechnung_notiz: e.target.value})} placeholder="Zahlungsziel, Rechnungsintervall…" /></label>
              </div>
            )}

            {msg && <p className="vd-form-msg">{msg}</p>}
            <div className="vd-modal-actions">
              <button className="vd-btn-sec" onClick={() => { setShowForm(false); setEditVerband(null); }}>Abbrechen</button>
              <button className="vd-btn-pri" onClick={handleSave} disabled={saving}>{saving ? '…' : 'Speichern'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Admin anlegen ── */}
      {showAdminForm && (
        <div className="vd-overlay" onClick={() => setShowAdminForm(null)}>
          <div className="vd-modal" onClick={e => e.stopPropagation()}>
            <h3>Admin-User anlegen</h3>
            <div className="vd-form">
              <label>Name<input value={adminForm.name} onChange={e => setAdminForm({...adminForm, name: e.target.value})} /></label>
              <label>E-Mail<input type="email" value={adminForm.email} onChange={e => setAdminForm({...adminForm, email: e.target.value})} /></label>
              <label>Benutzername<input value={adminForm.benutzername} onChange={e => setAdminForm({...adminForm, benutzername: e.target.value})} /></label>
              <label>Passwort<input type="password" value={adminForm.passwort} onChange={e => setAdminForm({...adminForm, passwort: e.target.value})} /></label>
            </div>
            {msg && <p className="vd-form-msg">{msg}</p>}
            <div className="vd-modal-actions">
              <button className="vd-btn-sec" onClick={() => setShowAdminForm(null)}>Abbrechen</button>
              <button className="vd-btn-pri" onClick={handleAdminSave} disabled={saving}>{saving ? '…' : 'Admin anlegen'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Rechnung erstellen ── */}
      {showRechnungForm && (
        <div className="vd-overlay" onClick={() => setShowRechnungForm(null)}>
          <div className="vd-modal" onClick={e => e.stopPropagation()}>
            <h3>Rechnung erstellen — {rechnungForm.verband_name}</h3>
            <div className="vd-form">

              {/* Turnier-Auswahl */}
              <label>Turnier (Gesamtrechnung)
                <select
                  value={rechnungForm.turnier_id || ''}
                  onChange={e => {
                    const tid = parseInt(e.target.value) || '';
                    const t   = currentTurniere.find(t => t.turnier_id === tid);
                    const newMenge = rechnungForm.abrechnung_typ === 'pro_teilnehmer'
                      ? (t?.anmeldungen_count || 0)
                      : rechnungForm.menge;
                    const newGesamt = ((parseFloat(rechnungForm.grundbetrag)||0) + (parseFloat(rechnungForm.rate)||0) * newMenge).toFixed(2);
                    setRechnungForm({...rechnungForm, turnier_id: tid, turnier_name: t?.name || '', menge: newMenge, betrag_gesamt: newGesamt });
                  }}
                >
                  <option value="">— Turnier wählen —</option>
                  {currentTurniere.map(t => (
                    <option key={t.turnier_id} value={t.turnier_id}>
                      {t.name} ({fmt(t.datum)}) · {t.anmeldungen_count} Anmeld.
                    </option>
                  ))}
                </select>
              </label>
              {currentTurniere.length === 0 && !loadingTurniere && (
                <div className="vd-no-abrechnung">Keine aktiven Turniere gefunden.</div>
              )}

              <label>Abrechnungsart
                <select value={rechnungForm.abrechnung_typ} onChange={e => setRechnungForm({...rechnungForm, abrechnung_typ: e.target.value})}>
                  <option value="fixbetrag">Fixbetrag</option>
                  <option value="pro_teilnehmer">Pro Teilnehmer</option>
                  <option value="pro_event">Pro Event</option>
                </select>
              </label>
              <div className="vd-form-row">
                <label>Grundbetrag (€)<input type="number" step="0.01" min="0" value={rechnungForm.grundbetrag} onChange={e => setRechnungForm({...rechnungForm, grundbetrag: e.target.value})} /></label>
                <label>Rate (€)<input type="number" step="0.01" min="0" value={rechnungForm.rate} onChange={e => setRechnungForm({...rechnungForm, rate: e.target.value})} /></label>
                <label>Menge<input type="number" min="0" value={rechnungForm.menge} onChange={e => setRechnungForm({...rechnungForm, menge: e.target.value})} /></label>
              </div>
              <div className="vd-rechnung-preview">
                Gesamt: <strong>{fmtEuro((parseFloat(rechnungForm.grundbetrag)||0) + (parseFloat(rechnungForm.rate)||0) * (parseInt(rechnungForm.menge)||0))}</strong>
                <button className="vd-link" style={{marginLeft:'0.75rem'}} onClick={() => setRechnungForm({...rechnungForm, betrag_gesamt: ((parseFloat(rechnungForm.grundbetrag)||0) + (parseFloat(rechnungForm.rate)||0) * (parseInt(rechnungForm.menge)||0)).toFixed(2)})}>Übernehmen</button>
              </div>
              <label>Rechnungsbetrag (€)<input type="number" step="0.01" min="0" value={rechnungForm.betrag_gesamt} onChange={e => setRechnungForm({...rechnungForm, betrag_gesamt: e.target.value})} /></label>
              <label>Fälligkeitsdatum<input type="date" value={rechnungForm.faellig_am} onChange={e => setRechnungForm({...rechnungForm, faellig_am: e.target.value})} /></label>
              <label>Notiz<input value={rechnungForm.notizen} onChange={e => setRechnungForm({...rechnungForm, notizen: e.target.value})} placeholder="Intern oder für den Verband…" /></label>
            </div>

            {/* Vorschau der zu erstellenden Rechnung */}
            <div style={{marginTop:'0.75rem'}}>
              <button className="vd-link" onClick={() => {
                const previewR = {
                  ...rechnungForm,
                  rechnungsnummer: 'TDA-INTL-' + new Date().getFullYear() + '-### (wird beim Speichern vergeben)',
                  erstellt_am: new Date().toISOString(),
                  status: 'entwurf',
                };
                setPreviewRechnung({ r: previewR, verband: verbaende.find(v => v.id === rechnungForm.verband_id) });
              }}>👁 Vorschau anzeigen</button>
            </div>

            {msg && <p className="vd-form-msg">{msg}</p>}
            <div className="vd-modal-actions">
              <button className="vd-btn-sec" onClick={() => setShowRechnungForm(null)}>Abbrechen</button>
              <button className="vd-btn-pri" onClick={handleRechnungSave} disabled={saving}>{saving ? '…' : 'Rechnung erstellen'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Rechnungsvorschau ── */}
      {previewRechnung && (
        <div className="vd-overlay" onClick={() => setPreviewRechnung(null)}>
          <div className="vd-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="vd-preview-header">
              <span className="vd-preview-title">Rechnungsvorschau — {previewRechnung.r.rechnungsnummer}</span>
              <div className="vd-preview-actions">
                <button className="vd-btn-pri" onClick={() => handlePrint(previewRechnung.r, previewRechnung.verband)}>🖨 Drucken</button>
                <button className="vd-btn-sec" onClick={() => setPreviewRechnung(null)}>✕ Schließen</button>
              </div>
            </div>
            <div className="vd-preview-body">
              <iframe
                srcDoc={buildInvoiceHTML(previewRechnung.r, previewRechnung.verband, sender)}
                style={{ width:'100%', height:'100%', border:'none' }}
                title="Rechnungsvorschau"
              />
            </div>
          </div>
        </div>
      )}

      <style>{`
        .vd-wrap { display:flex; flex-direction:column; min-height:0; }
        .vd-tabs { display:flex; align-items:center; gap:0.25rem; padding:1rem 1.5rem 0; border-bottom:1px solid var(--border,#2a2a4a); flex-shrink:0; }
        .vd-tab { padding:0.5rem 1rem; background:none; border:none; border-bottom:2px solid transparent; cursor:pointer; font-size:0.9rem; color:var(--text-muted,#888); white-space:nowrap; transition:all 0.15s; margin-bottom:-1px; }
        .vd-tab:hover { color:var(--text,#fff); }
        .vd-tab.active { color:var(--accent,#3b82f6); border-bottom-color:var(--accent,#3b82f6); font-weight:600; }
        .vd-tab-action { margin-left:auto; padding:0.4rem 1rem; background:var(--accent,#3b82f6); color:#fff; border:none; border-radius:6px; cursor:pointer; font-size:0.85rem; font-weight:600; }
        .vd-tab-action:hover { background:#2563eb; }
        .vd-msg { margin:0.75rem 1.5rem 0; padding:0.5rem 1rem; background:rgba(59,130,246,0.1); border:1px solid var(--accent,#3b82f6); border-radius:6px; color:var(--accent,#3b82f6); font-size:0.85rem; flex-shrink:0; }
        .vd-content { padding:1.25rem 1.5rem; }
        .vd-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; width:100%; }
        @media (max-width:1200px) { .vd-grid { grid-template-columns:repeat(3,1fr); } }
        @media (max-width:800px)  { .vd-grid { grid-template-columns:repeat(2,1fr); } }
        .vd-card { background:var(--bg-secondary,#0f0f1a); border:1px solid var(--border,#2a2a4a); border-radius:10px; padding:1.1rem; display:flex; flex-direction:column; gap:0.6rem; }
        .vd-card.inactive { opacity:0.55; }
        .vd-card-top { display:flex; gap:0.75rem; align-items:flex-start; }
        .vd-logo { width:36px; height:36px; object-fit:contain; border-radius:6px; flex-shrink:0; }
        .vd-card-info h3 { margin:0; font-size:1rem; }
        .vd-card-meta { display:flex; gap:0.4rem; align-items:center; margin-top:0.2rem; flex-wrap:wrap; }
        .vd-slug { color:var(--text-muted,#888); font-size:0.75rem; }
        .vd-plan { font-size:0.65rem; padding:0.1rem 0.35rem; border-radius:4px; font-weight:700; text-transform:uppercase; }
        .plan-free { background:#374151;color:#9ca3af; }
        .plan-starter { background:#1e3a5f;color:#60a5fa; }
        .plan-pro { background:#1e4d2b;color:#34d399; }
        .plan-enterprise { background:#4c1d95;color:#c4b5fd; }
        .vd-inactive { background:#7f1d1d;color:#fca5a5;font-size:0.65rem;padding:0.1rem 0.35rem;border-radius:4px; }
        .vd-demo { background:#713f12;color:#fbbf24;font-size:0.65rem;padding:0.1rem 0.35rem;border-radius:4px;font-weight:700; }
        .vd-subdomain { font-size:0.78rem; color:var(--text-muted,#888); font-family:monospace; }
        .vd-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:0.4rem; }
        .vd-s { background:var(--card-bg,#1a1a2e); border-radius:6px; padding:0.35rem; text-align:center; }
        .vd-s b { display:block; font-size:1rem; }
        .vd-s span { font-size:0.62rem; color:var(--text-muted,#888); }
        .vd-actions { display:flex; gap:0.4rem; }
        .vd-btn { flex:1; padding:0.4rem; border:none; border-radius:6px; cursor:pointer; font-size:0.78rem; font-weight:600; }
        .vd-btn.switch { background:#1e3a5f;color:#60a5fa; } .vd-btn.switch:hover{background:#2d5a9f;}
        .vd-btn.edit { background:#374151;color:#d1d5db; } .vd-btn.edit:hover{background:#4b5563;}
        .vd-btn.admin { background:#1e4d2b;color:#34d399; } .vd-btn.admin:hover{background:#2d6b3f;}
        .vd-loading,.vd-error { padding:2rem; text-align:center; color:var(--text-muted,#888); }
        .vd-error { color:#f87171; }
        /* Übersicht */
        .vd-stat-row { display:flex; gap:1rem; margin-bottom:1.5rem; flex-wrap:wrap; }
        .vd-stat { background:var(--bg-secondary,#0f0f1a); border:1px solid var(--border,#2a2a4a); border-radius:10px; padding:1rem 1.5rem; text-align:center; min-width:100px; }
        .vd-stat-val { display:block; font-size:1.8rem; font-weight:700; }
        .vd-stat-lbl { font-size:0.75rem; color:var(--text-muted,#888); }
        .vd-overview-list { display:flex; flex-direction:column; gap:0.5rem; }
        .vd-overview-row { display:flex; align-items:center; gap:1rem; background:var(--bg-secondary,#0f0f1a); border:1px solid var(--border,#2a2a4a); border-radius:8px; padding:0.6rem 1rem; flex-wrap:wrap; }
        .vd-ov-name { font-weight:600; min-width:140px; }
        .vd-ov-sub { font-size:0.78rem; color:var(--text-muted,#888); font-family:monospace; flex:1; }
        .vd-ov-num { font-size:0.82rem; color:var(--text-muted,#888); white-space:nowrap; }
        .vd-ov-bill { font-size:0.8rem; color:#34d399; }
        /* Finanzen */
        /* Finanzen Filter */
        .vd-fin-filter-bar { background:var(--bg-secondary,#0f0f1a); border:1px solid var(--border,#2a2a4a); border-radius:10px; padding:0.85rem 1rem; margin-bottom:1rem; }
        .vd-fin-filter-row { display:flex; flex-wrap:wrap; gap:0.5rem; align-items:center; margin-bottom:0.6rem; }
        .vd-fin-input { background:var(--card-bg,#1a1a2e); border:1px solid var(--border,#2a2a4a); border-radius:6px; color:var(--text,#fff); padding:0.35rem 0.65rem; font-size:0.82rem; min-width:120px; flex:1; }
        .vd-fin-input::placeholder { color:var(--text-muted,#888); }
        .vd-fin-input:focus { outline:none; border-color:var(--accent,#3b82f6); }
        .vd-fin-clear { padding:0.35rem 0.75rem; background:#7f1d1d; color:#fca5a5; border:none; border-radius:6px; cursor:pointer; font-size:0.8rem; font-weight:600; white-space:nowrap; }
        .vd-fin-clear:hover { background:#991b1b; }
        .vd-fin-alpha-bar { display:flex; flex-wrap:wrap; gap:0.25rem; }
        .vd-fin-alpha-btn { min-width:1.9rem; height:1.9rem; background:var(--card-bg,#1a1a2e); border:1px solid var(--border,#2a2a4a); border-radius:5px; color:var(--text-muted,#888); font-size:0.78rem; font-weight:700; cursor:pointer; transition:all 0.15s; display:flex; align-items:center; justify-content:center; }
        .vd-fin-alpha-btn:hover { background:var(--accent,#3b82f6); color:#fff; border-color:var(--accent,#3b82f6); }
        .vd-fin-alpha-btn.active { background:var(--accent,#3b82f6); color:#fff; border-color:var(--accent,#3b82f6); }
        .vd-fin-filter-hint { font-size:0.78rem; color:var(--text-muted,#888); margin-top:0.5rem; }
        .vd-fin-block { background:var(--bg-secondary,#0f0f1a); border:1px solid var(--border,#2a2a4a); border-radius:10px; padding:1.1rem; margin-bottom:1rem; }
        .vd-fin-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:0.75rem; flex-wrap:wrap; gap:0.5rem; }
        .vd-fin-name { font-weight:700; font-size:1rem; }
        .vd-fin-typ { font-size:0.75rem; background:#1e3a5f; color:#60a5fa; padding:0.1rem 0.4rem; border-radius:4px; }
        .vd-btn-rechnung { padding:0.35rem 0.85rem; background:var(--accent,#3b82f6); color:#fff; border:none; border-radius:6px; cursor:pointer; font-size:0.8rem; font-weight:600; }
        .vd-btn-rechnung:hover { background:#2563eb; }
        .vd-hochrechnung { background:var(--card-bg,#1a1a2e); border-radius:8px; padding:0.75rem 1rem; margin-bottom:0.75rem; display:flex; flex-direction:column; gap:0.3rem; }
        .vd-hr-row { display:flex; justify-content:space-between; align-items:center; font-size:0.85rem; }
        .vd-hr-lbl { color:var(--text-muted,#888); }
        .vd-hr-row.total { border-top:1px solid var(--border,#2a2a4a); margin-top:0.25rem; padding-top:0.35rem; }
        .vd-hr-row.total .vd-hr-val { font-size:1rem; color:#34d399; }
        .vd-hr-row.offen .vd-hr-val { color:#f87171; }
        .vd-no-abrechnung { font-size:0.85rem; color:var(--text-muted,#888); padding:0.5rem 0; }
        .vd-link { background:none; border:none; cursor:pointer; color:var(--accent,#3b82f6); font-size:inherit; padding:0; text-decoration:underline; }
        .vd-link-green { color:#10b981; }
        .vd-link-red { color:#ef4444; }
        .vd-status-badge { font-size:0.7rem; padding:0.15rem 0.4rem; border-radius:4px; font-weight:600; }
        .vd-fin-table { margin-top:0.5rem; }
        .vd-row-actions { display:flex; gap:0.65rem; align-items:center; white-space:nowrap; font-size:0.82rem; }
        .vd-rechnung-preview { font-size:0.9rem; background:var(--card-bg,#1a1a2e); border-radius:6px; padding:0.5rem 0.75rem; display:flex; align-items:center; }
        /* Placeholder */
        .vd-placeholder { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:200px; color:var(--text-muted,#888); text-align:center; }
        .vd-ph-icon { font-size:3rem; margin-bottom:0.75rem; }
        .vd-placeholder h3 { margin:0 0 0.5rem; color:var(--text,#fff); }
        /* Table */
        .vd-table { width:100%; border-collapse:collapse; }
        .vd-table th { text-align:left; padding:0.6rem 1rem; font-size:0.78rem; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted,#888); border-bottom:1px solid var(--border,#2a2a4a); }
        .vd-table td { padding:0.65rem 1rem; border-bottom:1px solid var(--border,#2a2a4a); font-size:0.88rem; }
        .vd-table tr:hover td { background:rgba(255,255,255,0.02); }
        /* Modal */
        .vd-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.7); display:flex; align-items:center; justify-content:center; z-index:100000; }
        .vd-modal { background:var(--card-bg,#1a1a2e); border:1px solid var(--border,#2a2a4a); border-radius:12px; padding:1.5rem; width:480px; max-width:95vw; max-height:85vh; overflow-y:auto; }
        .vd-modal h3 { margin:0 0 0.75rem; }
        .vd-modal-tabs { display:flex; gap:0.25rem; margin-bottom:1rem; border-bottom:1px solid var(--border,#2a2a4a); }
        .vd-modal-tab { padding:0.4rem 1rem; background:none; border:none; border-bottom:2px solid transparent; cursor:pointer; font-size:0.85rem; color:var(--text-muted,#888); margin-bottom:-1px; }
        .vd-modal-tab.active { color:var(--accent,#3b82f6); border-bottom-color:var(--accent,#3b82f6); font-weight:600; }
        .vd-form { display:flex; flex-direction:column; gap:0.65rem; margin-top:0.5rem; }
        .vd-form-row { display:flex; gap:0.65rem; }
        .vd-form-row label { flex:1; }
        .vd-form label { display:flex; flex-direction:column; gap:0.25rem; font-size:0.82rem; color:var(--text-muted,#888); }
        .vd-form input,.vd-form select { padding:0.45rem 0.7rem; background:var(--bg-secondary,#0f0f1a); border:1px solid var(--border,#2a2a4a); border-radius:6px; color:var(--text,#fff); font-size:0.88rem; }
        .vd-checkbox-label { flex-direction:row !important; align-items:center; gap:0.5rem !important; font-size:0.85rem !important; color:var(--text,#fff) !important; cursor:pointer; }
        .vd-checkbox-label input { width:auto; }
        .vd-form-msg { color:#60a5fa; font-size:0.83rem; margin:0.5rem 0 0; }
        .vd-modal-actions { display:flex; justify-content:flex-end; gap:0.75rem; margin-top:1.1rem; }
        .vd-btn-pri { padding:0.45rem 1.1rem; background:var(--accent,#3b82f6); color:#fff; border:none; border-radius:6px; cursor:pointer; font-weight:600; }
        .vd-btn-pri:hover{background:#2563eb;} .vd-btn-pri:disabled{opacity:.5;}
        .vd-btn-sec { padding:0.45rem 1.1rem; background:#374151; color:#d1d5db; border:none; border-radius:6px; cursor:pointer; }
        /* ── Auswertungen ── */
        .vd-aw-kpi-row { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-bottom:1rem; }
        .vd-aw-kpi { background:var(--bg-secondary,#0f0f1a); border:1px solid var(--border,#2a2a4a); border-radius:10px; padding:1rem 1.25rem; display:flex; flex-direction:column; gap:0.3rem; }
        .vd-aw-kpi.green  { border-left:3px solid #10b981; }
        .vd-aw-kpi.yellow { border-left:3px solid #f59e0b; }
        .vd-aw-kpi.blue   { border-left:3px solid #3b82f6; }
        .vd-aw-kpi.purple { border-left:3px solid #8b5cf6; }
        .vd-aw-kpi-val { font-size:1.4rem; font-weight:700; }
        .vd-aw-kpi-lbl { font-size:0.73rem; color:var(--text-muted,#888); }
        .vd-aw-grid { display:grid; grid-template-columns:1fr 1fr; gap:1rem; }
        .vd-aw-card { background:var(--bg-secondary,#0f0f1a); border:1px solid var(--border,#2a2a4a); border-radius:10px; padding:1rem 1.25rem; }
        .vd-aw-title { font-size:0.78rem; font-weight:700; text-transform:uppercase; letter-spacing:0.07em; color:var(--text-muted,#888); margin:0 0 0.8rem; }
        .vd-aw-bar-row { display:flex; align-items:center; gap:0.6rem; margin-bottom:0.55rem; font-size:0.82rem; }
        .vd-aw-bar-label { min-width:130px; max-width:130px; display:flex; align-items:center; gap:0.25rem; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
        .vd-aw-rank { color:var(--text-muted,#888); min-width:1.6rem; font-size:0.75rem; font-weight:700; }
        .vd-aw-bar-track { flex:1; height:9px; background:rgba(255,255,255,0.06); border-radius:5px; overflow:hidden; display:flex; gap:2px; }
        .vd-aw-bar-fill { height:100%; border-radius:5px; transition:width 0.5s ease; min-width:0; }
        .vd-aw-bar-fill.green  { background:linear-gradient(90deg,#059669,#10b981); }
        .vd-aw-bar-fill.yellow { background:linear-gradient(90deg,#d97706,#f59e0b); }
        .vd-aw-bar-fill.blue   { background:linear-gradient(90deg,#2563eb,#3b82f6); }
        .vd-aw-bar-val { min-width:70px; text-align:right; font-size:0.78rem; color:var(--text-muted,#888); }
        .vd-aw-legend { display:flex; align-items:center; gap:0.35rem; margin-top:0.5rem; font-size:0.75rem; color:var(--text-muted,#888); }
        .vd-aw-leg-dot { display:inline-block; width:9px; height:9px; border-radius:50%; }
        .vd-aw-leg-dot.green  { background:#10b981; }
        .vd-aw-leg-dot.yellow { background:#f59e0b; }
        .vd-aw-empty { font-size:0.82rem; color:var(--text-muted,#888); margin:0; }
        /* Monats-Chart */
        .vd-aw-months { display:flex; gap:0.75rem; align-items:flex-end; height:130px; padding-top:8px; }
        .vd-aw-month-col { flex:1; display:flex; flex-direction:column; align-items:center; gap:0.15rem; height:100%; }
        .vd-aw-month-bar-wrap { flex:1; width:100%; display:flex; align-items:flex-end; min-height:0; }
        .vd-aw-month-bar { width:100%; background:linear-gradient(180deg,#3b82f6,#1d4ed8); border-radius:4px 4px 0 0; transition:height 0.5s ease; }
        .vd-aw-month-val { font-size:0.65rem; color:var(--text-muted,#888); white-space:nowrap; }
        .vd-aw-month-lbl { font-size:0.68rem; color:var(--text-muted,#888); font-weight:700; }
        /* Pills */
        .vd-aw-pills { display:flex; flex-wrap:wrap; gap:0.5rem; }
        .vd-aw-pill { display:flex; align-items:center; gap:0.5rem; background:rgba(255,255,255,0.04); border:1px solid var(--border,#2a2a4a); border-radius:7px; padding:0.4rem 0.75rem; }
        .vd-aw-pill-cnt { font-size:0.78rem; color:var(--text-muted,#888); }
        @media (max-width:900px) {
          .vd-aw-kpi-row { grid-template-columns:repeat(2,1fr); }
          .vd-aw-grid { grid-template-columns:1fr; }
        }
        /* Vorschau-Modal */
        .vd-preview-modal { background:var(--card-bg,#1a1a2e); border:1px solid var(--border,#2a2a4a); border-radius:12px; width:780px; max-width:96vw; height:85vh; display:flex; flex-direction:column; overflow:hidden; }
        .vd-preview-header { display:flex; justify-content:space-between; align-items:center; padding:0.9rem 1.25rem; border-bottom:1px solid var(--border,#2a2a4a); flex-shrink:0; }
        .vd-preview-title { font-weight:600; font-size:0.95rem; }
        .vd-preview-actions { display:flex; gap:0.6rem; }
        .vd-preview-body { flex:1; overflow:hidden; background:#fff; }
      `}</style>
    </div>
  );
}

export default VerbaendeDashboard;
